import { isTablet, isMobile, isDesktop } from 'react-device-detect'

import Settings from 'helpers/settings'
import ApplicationHelper from 'helpers/application_helper'
import { isObject } from 'helpers/underscore'
import ProfileHelpers from 'helpers/profile_helpers'

const getDeviceType = () => {
  if (isTablet) return 'tablet'
  if (isMobile) return 'mobile'
  if (isDesktop) return 'desktop'

  const ua = navigator.userAgent
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet'
  }
  // TODO sometimes it defines mac m1 with Chrome as mobile device
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua))
    return 'mobile'
  return 'desktop'
}

// https://docs.google.com/spreadsheets/d/1U3Bzlkjmz9pVjH4ezD9E5_bedd5KeDJ4fXwBdAk_SRQ/edit#gid=0
export const actions = {
  share: 'share',
  projectCreateFunnel: {
    init: 'init_project_creation',
    lookingFor: 'submit_project_create_looking_for',
    type: 'submit_project_create_project_type',
    name: 'submit_project_create_name',
    rates: 'submit_project_create_rates',
    dates: 'submit_project_create_dates',
    time: 'submit_project_create_start_time',
    location: 'submit_project_create_location',
    image: 'submit_project_create_image',
    notes: 'submit_project_create_notes',
    summary: 'submit_project_create_summary',
    tos: 'submit_project_create_tos',
    create: 'create_project',
    // TODO remove after redesigning direct booking responsive
    filters: 'submit_project_create_filters',
  },
  // rest actions from projectCreateFunnel
  guestPostingFunnel: {
    init: 'init_project_creation',
    guestName: 'submit_user_create_name',
    guestOauth: 'submit_user_create_with_oauth',
    guestEmail: 'submit_user_create_with_email',
    guestGeneral: 'submit_user_create_general_info',
    guestPhone: 'submit_user_create_phone',
    guestCompany: 'submit_user_create_company_name',
    guestSocial: 'submit_user_create_social',
    shareReferral: 'share_referral',
    finalizeGuestPostingAB: 'finalize_guest_posting_ab',
  },
  userCreateFunnel: {
    init: 'init_user_creation',
    emailContinue: 'submit_user_create_with_email',
    generalInfo: 'submit_user_create_general_info',
    profile: 'submit_user_create_type',
    intention: 'submit_user_create_intent_role',
    location: 'submit_user_create_location',
    phone: 'submit_user_create_phone',
    company: 'submit_user_create_company_name',
    website: 'submit_user_create_website',
    social: 'submit_user_create_social',
    referral: 'submit_user_create_referral',
    additionalInfo: 'submit_user_create_additional_info',
    image: 'submit_user_logo',
    complete: 'complete_sign_up',
  },
  job: {
    acceptInvitation: 'accept_job_invitation',
    chooseApplicant: 'choose_applicant',
    selectPM: 'select_payment_method',
    confirmHire: 'confirm_job_hire',
  },
  clickButton: 'click_button',
  clickBannerToApp: 'click_banner_to_app',
  submitCalendlyForm: 'submit_calendly_form',
  search: { showFilters: 'show_filters', applyFilter: 'apply_filter', clickFilter: 'click_filter' },
}

const setUserConfig = (user = null, action) => {
  const user_id = user ? user.swipecast_user_id : null
  const profile_type = user?.profiles?.length ? ProfileHelpers.getProfileLabel(user.profiles[0]) : null

  console.log('setUserConfig', action && 'by ' + action, user_id, profile_type)
  window.gtag('config', process.env.REACT_APP_GA4_TOKEN, { user_id, profile_type })
}

const sendMetric = (action, params) => {
  const pristineParams = { client_type: getDeviceType(), ...params }

  if (!action) return console.warn('action missing')
  console.log('sendMetric', action, pristineParams)
  window.gtag('event', action, pristineParams)
}

const ScAnalytics = {
  sendMetric,
  setUserConfig,

  // JOB
  sendConfirmHire: (is_booking, payment_type, book_amount) =>
    sendMetric(actions.job.confirmHire, { book_amount, payment_type, is_booking }),

  // SIGN UP
  // signup_divided_steps_flow - Legacy flag for divided steps flow. It is now always true
  sendUserInitFunnel: (label, section, page, signup_divided_steps_flow = true) =>
    sendMetric(actions.userCreateFunnel.init, { signup_divided_steps_flow, page, label, section }),

  // PROJECT
  sendGuestPostingInitFunnel: (label, section, page) =>
    sendMetric(actions.guestPostingFunnel.init, { is_booking: false, guest_posting_flow: true, label, section, page }),

  sendProjectInitFunnel: (is_booking, section, page, guest_posting_flow = false) =>
    sendMetric(actions.projectCreateFunnel.init, { is_booking, page, section, guest_posting_flow }),

  sendProjectSummary: (is_booking, oauth, guest_posting_flow) =>
    sendMetric(actions.projectCreateFunnel.summary, { is_booking, oauth, guest_posting_flow }),

  sendProjectTOS: (is_booking, oauth, guest_posting_flow) =>
    sendMetric(actions.projectCreateFunnel.tos, { is_booking, oauth, guest_posting_flow }),

  // MISC
  sendShare: (method, content_type, item_id) => sendMetric(actions.share, { method, content_type, item_id }),
  sendClickBannerToApp: () => sendMetric(actions.clickBannerToApp),
  sendClickButton: (label, section, page) => sendMetric(actions.clickButton, { page, section, label }),

  // type: profile_type | location | social, value: string | { min: integer, max: integer }
  sendSearchApplyFilter(type, value) {
    sendMetric(actions.search.applyFilter, {
      ...(isObject(value) ? { min: value.min, max: value.max } : { value }),
      type,
      version: ApplicationHelper.isMobile ? 2 : Settings.desktopSearchVersion,
    })
  },

  // type: profile_type | location | social,
  sendSearchClickFilter(type) {
    sendMetric(actions.search.clickFilter, {
      type,
      version: ApplicationHelper.isMobile ? 2 : Settings.desktopSearchVersion,
    })
  },
  buildSendSearchClickFilter(type) {
    return () => this.sendSearchClickFilter(type)
  },
}

export default ScAnalytics
